import { CompanyType } from '@@types/api.types';
import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { getCompanyTypeLabel } from '@utils/company.util';
import { XIcon } from 'lucide-react';
import type { FC } from 'react';
import { INTEL_ORGANIZATION_COMPANY_TYPES } from '../intel-organizations-sort.util';

type IntelOrganizationsFilterBarProps = {
  onPageReset: () => void;
  search: string | null;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setType: (type: CompanyType | null) => Promise<URLSearchParams>;
  type: CompanyType | null;
};

const ALL_TYPES_VALUE = 'all';

export const IntelOrganizationsFilterBar: FC<IntelOrganizationsFilterBarProps> = (props) => {
  const { onPageReset, search, setSearch, setType, type } = props;
  const hasActiveFilters = Boolean(search?.trim()) || type !== null;

  const handleSearchChange = (value: string | null) => {
    void setSearch(value);
    onPageReset();
  };

  const handleTypeChange = (value: string) => {
    const selectedType = INTEL_ORGANIZATION_COMPANY_TYPES.find((companyType) => companyType === value) ?? null;

    void setType(value === ALL_TYPES_VALUE ? null : selectedType);
    onPageReset();
  };

  const handleClear = () => {
    void setSearch(null);
    void setType(null);
    onPageReset();
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
        <Select
          value={type ?? ALL_TYPES_VALUE}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger className="w-full md:w-[220px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES_VALUE}>All Types</SelectItem>
            {INTEL_ORGANIZATION_COMPANY_TYPES.map((companyType) => (
              <SelectItem
                key={companyType}
                value={companyType}
              >
                {getCompanyTypeLabel(companyType)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ClearableSearchInput
          className="w-full flex-1"
          clearValue={null}
          onChange={handleSearchChange}
          placeholder="Search Organizations"
          value={search}
        />
      </div>

      {hasActiveFilters && (
        <Button
          className="gap-2 self-start md:self-auto"
          onClick={handleClear}
          type="button"
          variant="outline"
        >
          <XIcon className="size-4" />
          Clear
        </Button>
      )}
    </div>
  );
};
